import React from "react";
import DocsLayout, { Section, Steps, Callout, JumpNav, AvailableTo } from "./DocsLayout";

const Img = ({ src, alt }: { src: string; alt: string }) => (
  <img src={src} alt={alt} style={{ width: "100%", borderRadius: 8, border: "1px solid #e2e8f0", margin: "16px 0", display: "block" }} />
);

const COLOR = "#059669";
const ALL_ROLES = ["admin", "admin_assistant", "hr", "recruiter", "finance", "employee"] as const;
const MANAGE_ROLES = ["admin", "admin_assistant"] as const;

export default function PeopleHR() {
  return (
    <DocsLayout>
      <div style={{ marginBottom: 32 }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          background: COLOR + "15", border: `1px solid ${COLOR}30`,
          borderRadius: 8, padding: "5px 14px", marginBottom: 16,
        }}>
          <span style={{ fontSize: 16 }}>🗂️</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: COLOR }}>People & HR</span>
        </div>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>People & HR</h1>
        <p style={{ fontSize: 15, color: "#64748b", maxWidth: 640, lineHeight: 1.6 }}>
          Everything to do with your people — leave, attendance, employee records, performance, and the
          day-to-day self-service tools everyone uses.
        </p>
      </div>

      <JumpNav color={COLOR} items={[
        { id: "leaves", label: "Leaves" },
        { id: "attendance", label: "Attendance" },
        { id: "timesheet", label: "Timesheet" },
        { id: "employees", label: "Employees & Org Chart" },
        { id: "performance", label: "Performance Reviews" },
        { id: "offboarding", label: "Offboarding" },
        { id: "assets", label: "Assets" },
        { id: "helpdesk", label: "Helpdesk" },
        { id: "referrals", label: "Referrals" },
        { id: "pulse", label: "Pulse Survey" },
        { id: "policies", label: "Policies" },
        { id: "announcements", label: "Announcements" },
        { id: "notifications", label: "Notifications" },
        { id: "hr-letters", label: "HR Letters" },
        { id: "reports", label: "Reports" },
      ]} />

      <Section id="leaves" title="Leaves">
        <AvailableTo roles={[...ALL_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Every employee views their balance and applies for leave; Admin and Admin Assistant approve
          requests and manage leave policy for the whole team.
        </p>
        <Img src="/docs/screenshots/employee-leaves.png" alt="My Leaves page" />
        <Steps items={[
          "Applying: click My Leaves in the sidebar. The top cards show your remaining balance per leave type.",
          "Click Apply for Leave, pick the leave type, start/end date, and a reason, then submit.",
          "Your manager receives an email; you'll see the request as Pending until approved or rejected.",
        ]} />
        <Img src="/docs/screenshots/aa-leaves.png" alt="Leave Management" />
        <Steps items={[
          "Approving: click Leaves in the sidebar — a badge shows the number of pending requests.",
          "Pending tab: review each request (employee, type, dates, reason). Click Approve or Reject — rejections require a reason.",
          "Team Overview tab: see all employees' leave status for the current month in a calendar grid.",
          "Leave Types tab: create or edit leave types (name, annual allowance, carry-forward rules).",
          "Public Holidays tab: add national/company holidays for the year.",
          "Balances tab: view and manually adjust an employee's leave balance if needed.",
        ]} />
        <Callout
          icon="⚠️"
          title="Self-approval blocked"
          body="Approvers cannot approve their own leave requests — the system blocks this. Another admin must approve those."
        />
      </Section>

      <Section id="attendance" title="Attendance">
        <AvailableTo roles={[...ALL_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Everyone marks their own daily attendance; Admin and Admin Assistant see and manage attendance for
          the whole team.
        </p>
        <Img src="/docs/screenshots/employee-attendance.png" alt="Attendance page" />
        <Steps items={[
          "Click Attendance in the sidebar, then Mark Present for today — you can only mark the current day.",
          "The Monthly Summary tab shows your present/absent/leave breakdown for the month.",
        ]} />
        <Img src="/docs/screenshots/aa-attendance.png" alt="Team Attendance" />
        <Steps items={[
          "Team view — Mark Team tab: see each employee's status for the selected date and set it in bulk, then Save Attendance.",
          "Monthly Summary tab: select an employee and month to see their full attendance breakdown.",
        ]} />
      </Section>

      <Section id="timesheet" title="Timesheet">
        <AvailableTo roles={[...ALL_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Log the hours you worked against a project or client, day by day.
        </p>
        <Img src="/docs/screenshots/employee-timesheet.png" alt="Timesheet page" />
        <Steps items={[
          "Click Timesheet in the sidebar.",
          "Add an entry: project name, client name (optional), hours worked, and any notes.",
          "Entries can be edited any time before the week is submitted.",
        ]} />
        <Callout
          icon="ℹ️"
          title="Contractors"
          body="If you're a contractor, your week shows a status banner — Draft, Submitted, or Approved. Once a week is submitted or approved, its entries are locked and can no longer be edited directly."
        />
      </Section>

      <Section id="employees" title="Employees & Org Chart">
        <AvailableTo roles={[...MANAGE_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Manage the employee directory — add new employees, edit HR fields, view documents, and handle
          bank details.
        </p>
        <Img src="/docs/screenshots/aa-employees.png" alt="Employees directory" />
        <Steps items={[
          "Click Employees in the sidebar.",
          "Invite individual employee: click Invite Employee, enter name, email, and role. They receive a setup link by email.",
          "Bulk import: click Import CSV, download the template, fill it in, and upload.",
          "Edit employee HR fields: click an employee's name → edit designation, department, joining date, reporting manager, and bank details.",
          "Upload documents: from the employee profile, upload offer letters, contracts, or IDs.",
          "Change role: use the Role dropdown on the employee profile.",
        ]} />
        <Callout
          icon="💡"
          title="Auto Employee ID"
          body="Each new employee gets an auto-generated ID based on your company prefix (e.g. SEQ-0001). This is set in Settings → Company."
        />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, margin: "24px 0 16px" }}>
          Org Chart visualises your company hierarchy. Filter by department, search by name, and switch
          between tree and list views.
        </p>
        <Img src="/docs/screenshots/aa-org-chart.png" alt="Org Chart" />
      </Section>

      <Section id="performance" title="Performance Reviews">
        <AvailableTo roles={[...ALL_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Admin and Admin Assistant create and manage review cycles; employees complete self-assessments.
        </p>
        <Img src="/docs/screenshots/aa-performance.png" alt="Performance Reviews" />
        <Steps items={[
          "Click New Review Cycle — set the name, review period, and which employees are included.",
          "Activate the cycle — employees will see it and can complete their self-assessment (star rating + goals).",
          "Once an employee submits their self-assessment, add the Manager Review.",
          "Close the cycle when all reviews are done.",
        ]} />
      </Section>

      <Section id="offboarding" title="Offboarding">
        <AvailableTo roles={[...ALL_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Any employee resigning submits it themselves; Admin and Admin Assistant manage the clearance
          checklist and final settlement.
        </p>
        <Img src="/docs/screenshots/employee-exit.png" alt="Offboarding page" />
        <Steps items={[
          "Employee: click Offboarding in the sidebar, then Submit Resignation — enter last working day, notice period, and reason.",
          "HR is notified immediately; you can track clearance progress and final settlement from the same page.",
        ]} />
        <Img src="/docs/screenshots/aa-exit.png" alt="Offboarding management" />
        <Steps items={[
          "Manager view: click an employee's offboarding record.",
          "Clearance Checklist: mark each of the 7 default items as complete (IT equipment returned, access revoked, etc.).",
          "Final Settlement: enter the settlement amount and any notes, then Complete Offboarding.",
        ]} />
        <Callout
          icon="⚠️"
          title="Once submitted"
          body="A resignation is visible to HR immediately. Ensure dates are correct before submitting — changes require HR assistance."
        />
      </Section>

      <Section id="assets" title="Assets">
        <AvailableTo roles={[...ALL_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Employees see equipment assigned to them; Admin and Admin Assistant manage the full inventory.
        </p>
        <Img src="/docs/screenshots/employee-assets.png" alt="Assets page" />
        <Img src="/docs/screenshots/aa-assets.png" alt="Asset management" />
        <Steps items={[
          "Manager view: click Assets in the sidebar, then New Asset — enter type (Laptop/Phone/Monitor/etc), name, serial number.",
          "Assign: click Assign on an available asset and select the employee.",
          "Unassign: click Unassign when the employee returns the equipment.",
        ]} />
      </Section>

      <Section id="helpdesk" title="Helpdesk">
        <AvailableTo roles={[...ALL_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Raise a ticket for any HR question or issue instead of emailing back and forth.
        </p>
        <Img src="/docs/screenshots/employee-helpdesk.png" alt="Helpdesk page" />
        <Steps items={[
          "Click Helpdesk in the sidebar, then + New Ticket.",
          "Enter a subject and description, choose a category, and set a priority if it's urgent.",
          "Track your ticket's status — Open, In Progress, or Resolved — from the same list.",
        ]} />
      </Section>

      <Section id="referrals" title="Referrals">
        <AvailableTo roles={[...ALL_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Refer a friend for an open role directly from HRxAI and track their progress through the hiring
          pipeline.
        </p>
        <Img src="/docs/screenshots/employee-referrals.png" alt="Refer a Friend page" />
        <Steps items={[
          "Click Referrals in the sidebar.",
          "Pick an open job and submit your friend's name, email, and CV.",
          "Watch their status move through the pipeline — you'll see if they're hired.",
        ]} />
      </Section>

      <Section id="pulse" title="Pulse Survey">
        <AvailableTo roles={[...ALL_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          One quick, completely anonymous question each month — a fast way to give honest feedback about how
          things are going.
        </p>
        <Img src="/docs/screenshots/employee-pulse.png" alt="Pulse Survey page" />
        <Steps items={[
          "Click Pulse Survey in the sidebar.",
          "Pick a score for this month's question and add an optional comment.",
          "Submit — nobody, including HR and Admin, can see who answered what.",
        ]} />
      </Section>

      <Section id="policies" title="Policies">
        <AvailableTo roles={[...ALL_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Browse and download your company's official policy documents.
        </p>
        <Img src="/docs/screenshots/employee-policies.png" alt="Policies page" />
        <Steps items={[
          "Click Policies in the sidebar.",
          "Browse the list of published policies and click one to view or download it.",
        ]} />
      </Section>

      <Section id="announcements" title="Announcements">
        <AvailableTo roles={[...ALL_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Company-wide news and updates posted by HR or Admin — pinned items always appear at the top.
        </p>
        <Img src="/docs/screenshots/employee-announcements.png" alt="Announcements page" />
        <Steps items={[
          "Click Announcements in the sidebar.",
          "Filter by category (General, Policy, Event, Holiday, Urgent) using the tabs at the top.",
          "📌 marks a pinned announcement — these stay at the top regardless of date.",
        ]} />
      </Section>

      <Section id="notifications" title="Notifications">
        <AvailableTo roles={[...ALL_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          The bell icon in the top navigation bar shows real-time alerts — leave approvals, new
          announcements, and other HR activity relevant to you.
        </p>
        <Steps items={[
          "Click the bell icon (top of any page) to open your notifications.",
          "A red badge shows your unread count.",
          "Click a notification to mark it read, or Mark all read to clear the badge.",
        ]} />
      </Section>

      <Section id="hr-letters" title="HR Letters">
        <AvailableTo roles={[...MANAGE_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Generate AI-drafted HR letters for employees. Nine letter types are available: Appointment,
          Contract, Increment, Promotion, Experience Certificate, Relieving, Salary Certificate, NOC, Transfer.
        </p>
        <Steps items={[
          "Go to Employees → select an employee → HR Letters tab.",
          "Select the letter type.",
          "Click AI Draft — the letter is generated using the employee's data and company branding.",
          "Edit the letter in the browser if needed, then download as PDF.",
        ]} />
      </Section>

      <Section id="reports" title="Reports">
        <AvailableTo roles={[...MANAGE_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Three built-in report tabs give you a company-wide view of headcount, payroll costs, and leave usage.
        </p>
        <Img src="/docs/screenshots/aa-reports.png" alt="Reports page" />
        <ul style={{ paddingLeft: 20, color: "#475569", fontSize: 14, lineHeight: 2 }}>
          <li><strong>Headcount</strong> — total employees, active/pending/on-probation counts, department and role breakdown</li>
          <li><strong>Payroll Cost</strong> — monthly payroll spend as a bar chart</li>
          <li><strong>Leave Utilization</strong> — per leave-type progress bars showing used vs. available</li>
        </ul>
      </Section>
    </DocsLayout>
  );
}
